import { AnimatePresence, motion } from "framer-motion";
import PropTypes from "prop-types";
import { useCallback, useMemo, useState } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { TEST_IDS } from "../../constants/testIds";
import { useCanvas } from "../../contexts/CanvasContext";
import { cn } from "../../lib/utils";
import { Skeleton } from "../ui/skeleton";
import CanvasToolbar from "./CanvasToolbar";
import CodeEditor from "./editors/CodeEditor";
import CodePreview from "./editors/CodePreview";
import SpreadsheetEditor from "./editors/SpreadsheetEditor";
import TextEditor from "./editors/TextEditor";

const CanvasInterface = ({ className }) => {
  const { canvasState, showPreview, updateContent, closeCanvas } = useCanvas();
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // Check if we have meaningful content to display
  const hasContent = useMemo(() => {
    // For spreadsheet type, check if we have CSV data
    if (canvasState.type === "spreadsheet") {
      if (canvasState.content && typeof canvasState.content === "string") {
        const trimmedContent = canvasState.content.trim();
        // Check if it looks like CSV data (has commas and at least one line)
        return (
          trimmedContent.length > 0 &&
          (trimmedContent.includes(",") || trimmedContent.includes("\n"))
        );
      }
      return false;
    }

    // Check if canvas has blocks with content
    if (canvasState.blocks && canvasState.blocks.length > 0) {
      return canvasState.blocks.some((block) => {
        if (block.content && Array.isArray(block.content)) {
          return block.content.some(
            (item) =>
              item.type === "text" && item.text && item.text.trim().length > 0
          );
        }
        return false;
      });
    }

    // Check if canvas has string content
    if (canvasState.content && typeof canvasState.content === "string") {
      try {
        // Try to parse as JSON (might be stringified blocks)
        const parsed = JSON.parse(canvasState.content);
        if (Array.isArray(parsed)) {
          return parsed.some((block) => {
            if (block.content && Array.isArray(block.content)) {
              return block.content.some(
                (item) =>
                  item.type === "text" &&
                  item.text &&
                  item.text.trim().length > 0
              );
            }
            return false;
          });
        }
      } catch {
        // Not JSON, check as plain text
        return canvasState.content.trim().length > 0;
      }
    }

    return false;
  }, [canvasState.blocks, canvasState.content, canvasState.type]);

  const handleContentChange = useCallback(
    (newContent) => {
      updateContent(newContent);
    },
    [updateContent]
  );

  const handleClose = useCallback(() => {
    console.log("🎨 [CanvasInterface] handleClose called");
    closeCanvas();
  }, [closeCanvas]);

  const renderEditor = () => {
    switch (canvasState.type) {
      case "code":
        // Show preview if showPreview is true, otherwise show code editor
        if (showPreview) {
          return (
            <CodePreview
              content={canvasState.content}
              language={canvasState.language}
            />
          );
        } else {
          return (
            <CodeEditor
              key={canvasState.sourceMessageId || canvasState.name || "default"}
              content={canvasState.content}
              language={canvasState.language}
              onChange={handleContentChange}
              readOnly={false}
            />
          );
        }
      case "spreadsheet":
        return (
          <SpreadsheetEditor
            key={canvasState.sourceMessageId || canvasState.name || "default"}
            content={canvasState.content}
            onChange={handleContentChange}
            readOnly={false}
          />
        );
      case "markdown":
      case "html":
      case "text":
      default:
        return (
          <TextEditor
            key={canvasState.sourceMessageId || canvasState.name || "default"}
            content={canvasState.content}
            onChange={handleContentChange}
            readOnly={false}
          />
        );
    }
  };

  const renderSkeletonLoader = () => {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="h-full w-full p-4 space-y-4"
        style={{
          backgroundColor: document.documentElement.classList.contains("dark")
            ? "#2F2F2F"
            : "#FAFAFA",
        }}
      >
        {/* Loading skeleton for canvas content */}
        <div className="space-y-3">
          <Skeleton className="h-8 w-2/3 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-full bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-4/5 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
        </div>

        <div className="space-y-3 mt-8">
          <Skeleton className="h-6 w-3/4 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-5/6 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-2/3 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
        </div>

        <div className="space-y-3 mt-8">
          <Skeleton className="h-6 w-1/2 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-full bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-3/5 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
        </div>

        <div className="space-y-3 mt-8">
          <Skeleton className="h-6 w-4/5 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
          <Skeleton className="h-6 w-2/3 bg-gray-600/30 dark:bg-gray-600/30 bg-gray-300/30" />
        </div>
      </motion.div>
    );
  };

  const renderContent = () => {
    return (
      <AnimatePresence mode="wait">
        {hasContent ? (
          <motion.div
            key="editor-content"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="h-full"
          >
            {renderEditor()}
          </motion.div>
        ) : (
          <motion.div
            key="skeleton-content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="h-full"
          >
            {renderSkeletonLoader()}
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  if (!canvasState.isOpen) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
      className={cn("h-full flex flex-col", className)}
      data-testid={TEST_IDS.CANVAS_INTERFACE}
      style={{
        backgroundColor: document.documentElement.classList.contains("dark")
          ? "#2F2F2F"
          : "#FAFAFA",
      }}
    >
      {/* Toolbar with slide-down animation */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut", delay: 0.1 }}
      >
        <CanvasToolbar
          onClose={handleClose}
          onToggleVersionHistory={() =>
            setShowVersionHistory(!showVersionHistory)
          }
          showVersionHistory={showVersionHistory}
        />
      </motion.div>

      {/* Main content area */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
        className="flex-1 overflow-auto"
        style={{
          backgroundColor: document.documentElement.classList.contains("dark")
            ? "#2F2F2F"
            : "#FAFAFA",
        }}
      >
        <AnimatePresence mode="wait">
          {showVersionHistory ? (
            <motion.div
              key="version-history-layout"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-full"
            >
              <PanelGroup direction="horizontal">
                <Panel defaultSize={70} minSize={50}>
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                    className="h-full overflow-auto"
                    style={{
                      backgroundColor:
                        document.documentElement.classList.contains("dark")
                          ? "#2F2F2F"
                          : "#FAFAFA",
                    }}
                  >
                    {renderContent()}
                  </motion.div>
                </Panel>

                <PanelResizeHandle className="w-1 bg-gray-600 dark:bg-gray-600 bg-gray-300 hover:bg-gray-500 dark:hover:bg-gray-500 hover:bg-gray-400 transition-colors" />

                <Panel defaultSize={30} minSize={20}>
                  <motion.div
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    className="h-full p-4"
                    style={{
                      backgroundColor:
                        document.documentElement.classList.contains("dark")
                          ? "#2F2F2F"
                          : "#FAFAFA",
                    }}
                  >
                    <h3 className="text-sm font-medium mb-4 text-white dark:text-white text-gray-900">
                      Version History
                    </h3>
                    <p className="text-xs text-gray-400 dark:text-gray-400 text-gray-600">
                      Coming soon...
                    </p>
                  </motion.div>
                </Panel>
              </PanelGroup>
            </motion.div>
          ) : (
            <motion.div
              key="main-content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="h-full overflow-auto"
              style={{
                backgroundColor: document.documentElement.classList.contains(
                  "dark"
                )
                  ? "#2F2F2F"
                  : "#FAFAFA",
              }}
            >
              {renderContent()}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

CanvasInterface.propTypes = {
  className: PropTypes.string,
};

export default CanvasInterface;
